import React, { useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useToast as useRocketToast } from '@totalsoft/rocket-ui';
import { useDispatch } from 'react-redux';
import { useSelector, shallowEqual } from 'react-redux';
import { addNotification } from 'actions/actionCreators/notificationActions';
import RefreshPageToast from './RefreshPageToast';
import ErrorToast from './ErrorToast';
import WarningToast from './WarningToast';

export const useToast = () => {
  const dispatch = useDispatch();
  const refreshPageToastId = useRef(null);
  const addToast = useRocketToast();
  const hasRightsToViewLogs = useSelector(state => state.globalRights.menuLogs, shallowEqual);

  const addRefreshPageToast = useCallback(
    (message, opts) => {
      if (refreshPageToastId.current) return; //toast is already displayed
      const _opts = {
        ...opts,
        onDismiss: () => {
          refreshPageToastId.current = null;
        }
      };
      const events = {
        onDismiss: () => {
          if (refreshPageToastId.current) {
            toast.dismiss(refreshPageToastId.current);
            refreshPageToastId.current = null;
          }
        }
      };
      refreshPageToastId.current = opts?.toastId;
      addToast(<RefreshPageToast message={message} events={events} />, 'info', _opts);
    },
    [addToast]
  );

  const addInfoToast = useCallback(
    (message, autoClose, refreshPage, shouldAddNotification) => {
      const opts = {
        autoClose: autoClose ?? true,
        toastId: 'refreshPageToast'
      };

      if (refreshPage) {
        addRefreshPageToast(message, opts);
      } else {
        addToast(<span>{message}</span>, 'info', opts);
      }
      if (shouldAddNotification) dispatch(addNotification(message, message, false, false));
    },
    [addRefreshPageToast, dispatch, addToast]
  );

  const addErrorToast = useCallback(
    (message, correlationId, autoClose) => {
      const opts = {
        autoClose: autoClose
      };
      const toastId = `error-toast-${crypto.randomUUID()}`;
      addToast(
        <ErrorToast toastId={toastId} message={message} correlationId={correlationId} viewLogs={hasRightsToViewLogs} />,
        'error',
        opts
      );
    },
    [hasRightsToViewLogs, addToast]
  );

  const fn = useCallback(
    (message, variant, correlationId, autoClose = true, refreshPage = false, shouldAddNotification = false) => {
      switch (variant) {
        case 'error':
          addErrorToast(message, correlationId, autoClose);
          break;
        case 'info':
          addInfoToast(message, autoClose, refreshPage, shouldAddNotification);
          break;
        case 'warning':
          addToast(<WarningToast message={message} correlationId={correlationId} viewLogs={hasRightsToViewLogs} />, 'warning', {
            autoClose
          });
          break;
        case 'success':
          addToast(message, 'success', { autoClose });
          break;
        default:
          addToast(message, 'info', { autoClose });
          break;
      }
    },
    [addInfoToast, addErrorToast, hasRightsToViewLogs, addToast]
  );

  return fn;
};
